import React, { useState } from "react";
import { LogIn, Mail, Lock, AlertCircle, Scale, Key, ArrowLeft, Check } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

function formatError(detail) {
  if (!detail) return "Giriş yapılamadı";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const [mode, setMode] = useState("login"); // login | forgot | reset
  const [email, setEmail] = useState("");

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" style={{backgroundImage: "radial-gradient(circle at 15% 20%, rgba(220,38,38,0.14) 0%, transparent 55%), radial-gradient(circle at 85% 80%, rgba(30,58,138,0.4) 0%, transparent 50%)"}}>
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-6">
          <img src="/akat-logo.png" alt="Akat Hukuk" className="h-14 w-auto rounded-lg shadow-lg" />
          <div className="text-white">
            <div className="font-display text-2xl font-extrabold tracking-tight">BordroMat</div>
            <div className="text-[11px] text-slate-300 uppercase tracking-wider font-semibold">Akat Hukuk · Bordro Yönetim</div>
          </div>
        </div>

        {mode === "login" && (
          <LoginForm
            onForgot={(e) => { setEmail(e); setMode("forgot"); }}
            email={email}
            setEmail={setEmail}
          />
        )}
        {mode === "forgot" && (
          <ForgotForm
            email={email}
            setEmail={setEmail}
            onBack={() => setMode("login")}
            onSent={() => setMode("reset")}
          />
        )}
        {mode === "reset" && (
          <ResetForm
            email={email}
            onBack={() => setMode("forgot")}
            onDone={() => setMode("login")}
          />
        )}
      </div>
    </div>
  );
}

function LoginForm({ onForgot, email, setEmail }) {
  const { login } = useAuth();
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(() => {
    const r = sessionStorage.getItem("logout_reason") || "";
    sessionStorage.removeItem("logout_reason");
    return r;
  });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try { await login(email.trim(), password); }
    catch (ex) { setErr(formatError(ex?.response?.data?.detail) || ex.message); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl border border-slate-200 p-6 md:p-8 space-y-4" data-testid="login-form">
      <h1 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
        <LogIn size={20} /> Giriş Yap
      </h1>
      <p className="text-sm text-slate-500 -mt-2">Yönetici, muhasebeci veya personel hesabınızla oturum açın</p>
      {err && (
        <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-800" data-testid="login-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{err}</span>
        </div>
      )}
      <LabeledInput label="E-posta" icon={Mail} testid="login-email" type="email" required autoComplete="email" value={email} onChange={setEmail} placeholder="ornek@akathukuk.com" />
      <LabeledInput label="Şifre" icon={Lock} testid="login-password" type="password" required autoComplete="current-password" value={password} onChange={setPassword} />
      <button data-testid="login-submit" type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-lg font-semibold text-sm disabled:opacity-50 shadow-md">
        <LogIn size={14} /> {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
      </button>
      <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
        <button type="button" data-testid="forgot-password-link" onClick={() => onForgot(email)} className="text-xs font-semibold text-slate-700 hover:text-slate-900 flex items-center gap-1">
          <Key size={10} /> Şifremi Unuttum
        </button>
        <span className="text-[11px] text-slate-400 flex items-center gap-1"><Scale size={10} />Yetkili olmayan erişim kayda alınır</span>
      </div>
    </form>
  );
}

function ForgotForm({ email, setEmail, onBack, onSent }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      onSent();
    } catch (ex) {
      setErr(formatError(ex?.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl border border-slate-200 p-6 md:p-8 space-y-4" data-testid="forgot-form">
      <button type="button" onClick={onBack} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"><ArrowLeft size={12} /> Girişe dön</button>
      <h1 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
        <Key size={18} /> Şifremi Unuttum
      </h1>
      <p className="text-sm text-slate-500 -mt-2">Hesabınızın email adresini girin — 6 haneli sıfırlama kodu gönderelim.</p>
      {err && (
        <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-800">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />{err}
        </div>
      )}
      <LabeledInput label="E-posta" icon={Mail} testid="forgot-email" type="email" required value={email} onChange={setEmail} />
      <button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-lg font-semibold text-sm disabled:opacity-50">
        {loading ? "Gönderiliyor..." : "Kodu Gönder"}
      </button>
    </form>
  );
}

function ResetForm({ email, onBack, onDone }) {
  const [otp, setOtp] = useState("");
  const [np, setNp] = useState("");
  const [np2, setNp2] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (np !== np2) { setErr("Şifreler eşleşmiyor"); return; }
    if (np.length < 10 || !/[A-Za-zÇĞİÖŞÜçğıöşü]/.test(np) || !/\d/.test(np)) { setErr("Şifre en az 10 karakter olmalı ve en az bir harf ile bir rakam içermeli"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, otp: otp.trim(), new_password: np });
      setSuccess(true);
      setTimeout(onDone, 1500);
    } catch (ex) {
      setErr(formatError(ex?.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };
  if (success) {
    return (
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-6 md:p-8 text-center space-y-3" data-testid="reset-success">
        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 text-emerald-700 grid place-items-center"><Check size={24} /></div>
        <h2 className="font-display text-lg font-bold text-slate-900">Şifre Güncellendi</h2>
        <p className="text-sm text-slate-500">Yeni şifrenizle giriş yapabilirsiniz.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl border border-slate-200 p-6 md:p-8 space-y-4" data-testid="reset-form">
      <button type="button" onClick={onBack} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"><ArrowLeft size={12} /> Geri</button>
      <h1 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
        <Key size={18} /> Yeni Şifre Belirle
      </h1>
      <p className="text-sm text-slate-500 -mt-2"><strong>{email}</strong> adresine gönderilen 6 haneli kodu ve yeni şifrenizi girin.</p>
      {err && (
        <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-800" data-testid="reset-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />{err}
        </div>
      )}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">6 Haneli Kod</label>
        <input
          data-testid="reset-otp"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="123456"
          maxLength={6}
          inputMode="numeric"
          className="w-full px-3 py-3 border border-slate-200 rounded-lg text-center font-mono text-xl tracking-widest focus:outline-none focus:ring-2 focus:ring-slate-900"
        />
      </div>
      <LabeledInput label="Yeni Şifre" icon={Lock} testid="reset-new-password" type="password" required value={np} onChange={setNp} />
      <LabeledInput label="Yeni Şifre (Tekrar)" icon={Lock} testid="reset-new-password2" type="password" required value={np2} onChange={setNp2} />
      <button data-testid="reset-submit" type="submit" disabled={loading || otp.length !== 6} className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-lg font-semibold text-sm disabled:opacity-50">
        {loading ? "Güncelleniyor..." : "Şifreyi Güncelle"}
      </button>
    </form>
  );
}

function LabeledInput({ label, icon: Icon, testid, type = "text", required, value, onChange, placeholder, autoComplete }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />}
        <input
          data-testid={testid}
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={`w-full ${Icon ? "pl-9" : "pl-3"} pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900`}
        />
      </div>
    </div>
  );
}
